/**
 * Sessão do usuário.
 *
 * O token vive num cookie httpOnly: o front nunca o vê nem o guarda. Saber se
 * há sessão é uma pergunta ao servidor (`GET /api/auth/me`), não uma leitura de
 * localStorage — que era como o sistema antigo fazia e é o que um XSS captura.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api.ts';
import { apagarDadosGuardados } from '../lib/pwa.ts';

export type Papel =
  | 'admin'
  | 'diretor'
  | 'gerente_regional'
  | 'consultor'
  | 'pos_vendas'
  | 'financeiro'
  | 'rastreamento';

export interface Usuario {
  id: string;
  nome: string;
  /** Nulo para quem entra só por CPF. */
  email: string | null;
  cpf: string | null;
  papel: Papel;
  regionalId: string | null;
  codigoVoluntario: string | null;
  precisaTrocarSenha: boolean;
}

interface AuthCtx {
  usuario: Usuario | null;
  carregando: boolean;
  /** `identificador` é o CPF ou o e-mail — o servidor decide qual pelo formato. */
  entrar: (identificador: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  /** Verdadeiro se o papel do usuário está entre os informados. */
  pode: (...papeis: Papel[]) => boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    api
      .get<{ usuario: Usuario }>('/auth/me')
      .then((r) => setUsuario(r.usuario))
      .catch((e) => {
        // 401 aqui é o caso normal de quem ainda não entrou.
        if (!(e instanceof ApiError && e.status === 401)) console.error(e);
        setUsuario(null);
      })
      .finally(() => setCarregando(false));
  }, []);

  const entrar = useCallback(async (identificador: string, senha: string) => {
    const r = await api.post<{ usuario: Usuario }>('/auth/login', { identificador, senha });
    await apagarDadosGuardados();
    setUsuario(r.usuario);
  }, []);

  const sair = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    await apagarDadosGuardados();
    setUsuario(null);
  }, []);

  const pode = useCallback(
    (...papeis: Papel[]) => Boolean(usuario && papeis.includes(usuario.papel)),
    [usuario],
  );

  return (
    <Ctx.Provider value={{ usuario, carregando, entrar, sair, pode }}>{children}</Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth precisa estar dentro de AuthProvider');
  return ctx;
}
